import React from 'react';
import { useTheme } from '../context/ThemeContext';
import { Theme } from '../theme/themes';

export default function ThemeSelector() {
  const { colorTheme, setColorTheme, availableColorThemes } = useTheme();

  return (
    <div className="space-y-3">
      <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">
        Color Theme
      </h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {availableColorThemes.map((theme) => (
          <ThemeCard
            key={theme.id}
            theme={theme}
            isSelected={colorTheme.id === theme.id}
            onSelect={() => setColorTheme(theme.id)}
          />
        ))}
      </div>
    </div>
  );
}

interface ThemeCardProps {
  theme: Theme;
  isSelected: boolean;
  onSelect: () => void;
}

function ThemeCard({ theme, isSelected, onSelect }: ThemeCardProps) {
  return (
    <button
      onClick={onSelect}
      className={`relative p-4 rounded-xl border-2 transition-all duration-200 text-left ${
        isSelected
          ? 'border-primary-600 bg-primary-50 dark:bg-primary-900/20 shadow-md'
          : 'border-gray-200 dark:border-secondary-700 bg-white dark:bg-secondary-800 hover:border-primary-400 hover:shadow-sm'
      }`}
    >
      {/* Selected indicator */}
      {isSelected && (
        <div className="absolute top-3 right-3 flex items-center justify-center w-6 h-6 bg-primary-600 rounded-full">
          <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
      )}

      {/* Theme name */}
      <div className="mb-3">
        <h4 className="font-semibold text-gray-900 dark:text-white">{theme.name}</h4>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{theme.description}</p>
      </div>

      {/* Color palette preview */}
      <div className="flex gap-1.5">
        <div
          className="w-8 h-8 rounded-lg shadow-sm ring-1 ring-black/10"
          style={{ backgroundColor: `rgb(${theme.colors.primary[500]})` }}
          title="Primary 500"
        />
        <div
          className="w-8 h-8 rounded-lg shadow-sm ring-1 ring-black/10"
          style={{ backgroundColor: `rgb(${theme.colors.primary[600]})` }}
          title="Primary 600"
        />
        <div
          className="w-8 h-8 rounded-lg shadow-sm ring-1 ring-black/10"
          style={{ backgroundColor: `rgb(${theme.colors.secondary[700]})` }}
          title="Secondary 700"
        />
        <div
          className="w-8 h-8 rounded-lg shadow-sm ring-1 ring-black/10"
          style={{ backgroundColor: `rgb(${theme.colors.secondary[300]})` }}
          title="Secondary 300"
        />
      </div>
    </button>
  );
}
